import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { SITE_CONFIG } from "../config";

const LOGO_URL = "/assets/logo-circle.svg";

export const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#EFEAD8] border-t border-[#E6E2D6]" data-testid="main-footer">
      {/* Large brand statement */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
        <h2 className="text-3xl md:text-5xl font-serif font-light tracking-tight text-[#1C2E24]/15 leading-none select-none">
          Live Outside
        </h2>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img src={LOGO_URL} alt="Circle" className="h-12 w-12 rounded-full object-cover" style={{ filter: 'invert(1)' }} />
              <div>
                <span className="block text-lg font-serif tracking-wider text-[#1C2E24]">CIRCLE</span>
                <span className="block text-xs tracking-[0.15em] text-[#547055] uppercase">Natural Editing</span>
              </div>
            </Link>
            <p className="text-sm text-[#547055] leading-relaxed mb-6 font-sans">
              {t('footer.description')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs font-sans font-bold uppercase tracking-[0.2em] text-[#849C8A] mb-6">{t('footer.navigation')}</h4>
            <nav className="flex flex-col gap-4">
              <Link to="/portfolio" className="footer-link">Portfolio</Link>
              <Link to="/servicios" className="footer-link">{t('nav.servicios')}</Link>
              <Link to="/planes" className="footer-link">{t('nav.planes')}</Link>
              <Link to="/blog" className="footer-link">Blog</Link>
              <Link to="/contacto" className="footer-link">{t('nav.contacto')}</Link>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-sans font-bold uppercase tracking-[0.2em] text-[#849C8A] mb-6">{t('footer.services')}</h4>
            <nav className="flex flex-col gap-4">
              <Link to="/servicios#diseno" className="footer-link">{t('services.service1.title')}</Link>
              <Link to="/servicios#proyectos" className="footer-link">{t('services.service2.title')}</Link>
              <Link to="/servicios#mantenimiento" className="footer-link">{t('services.service3.title')}</Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-sans font-bold uppercase tracking-[0.2em] text-[#849C8A] mb-6">{t('footer.contact')}</h4>
            <div className="flex flex-col gap-4">
              <a href="https://maps.google.com/?q=San+Jose+del+Cabo" target="_blank" rel="noopener noreferrer" className="footer-link flex items-start gap-3">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <span>San José del Cabo, BCS, México</span>
              </a>
              <a href={`tel:+${SITE_CONFIG.phone}`} className="footer-link flex items-center gap-3">
                <Phone size={16} /><span>{SITE_CONFIG.phoneFormatted}</span>
              </a>
              <a href={`mailto:${SITE_CONFIG.email}`} className="footer-link flex items-center gap-3">
                <Mail size={16} /><span>{SITE_CONFIG.email}</span>
              </a>
            </div>
            <div className="flex items-center gap-4 mt-8">
              <a href={SITE_CONFIG.social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-[#E6E2D6] text-[#849C8A] hover:text-[#547055] hover:border-[#849C8A] transition-colors" data-testid="social-instagram">
                <Instagram size={18} />
              </a>
              <a href={SITE_CONFIG.social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-[#E6E2D6] text-[#849C8A] hover:text-[#547055] hover:border-[#849C8A] transition-colors" data-testid="social-facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#E6E2D6] mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#849C8A] font-sans">
            © {new Date().getFullYear()} El Círculo Landscape ARQ. {t('footer.rights')}
          </p>
          <Link to="/admin/login" className="text-xs text-[#849C8A]/40 hover:text-[#849C8A] transition-colors font-sans">Admin</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
